import type { ServerMessage } from '@archon3d/core';

type Started = Extract<ServerMessage, { t: 'started' }>;
type Sync = Extract<ServerMessage, { t: 'sync' }>;

/** A game to put on the table: the server's start and the catch-up that followed it. */
export interface GameStart {
  code: string;
  started: Started;
  sync: Sync;
}

/**
 * Which `started` and `sync` pairs put a new game on the table. The server sends the pair when a
 * game starts and again whenever a membership is resumed. After a reload that rebuilds the game;
 * after a connection blip in the same tab the game is already on the table, and the session
 * listening to the same room applies what the sync adds by itself (a duel's bundles included).
 * Building a second session there would strand the running duel on the first one's bundles.
 */
export class GameStarts {
  private pending: Started | null = null;
  private playing: string | null = null;

  /** The room whose game is on the table, if any. */
  get current(): string | null {
    return this.playing;
  }

  started(event: Started, code: string | undefined): void {
    if (code !== undefined && code === this.playing) return;
    this.pending = event;
  }

  /** The game to build, when this sync completes a start for a room not already on the table. */
  sync(event: Sync, code: string | undefined): GameStart | null {
    const started = this.pending;
    if (!started || code === undefined || code === this.playing) return null;
    this.pending = null;
    this.playing = code;
    return { code, started, sync: event };
  }

  /** The table is given up (left, replaced by a replay or another room): a start builds again. */
  reset(): void {
    this.pending = null;
    this.playing = null;
  }
}
